import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { API_BASE_URL as BASE_URL } from '../services/apiBaseUrl';

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------
const authHeaders = (token, tenantId) => ({
  'Authorization': `Bearer ${token}`,
  'X-Tenant-ID':   tenantId,
  'Content-Type':  'application/json',
});

const fetchAllPrescriptions = async (token, tenantId) => {
  // Try /all first (ADMIN endpoint); fall back to /prescriptions until backend adds it
  const res = await fetch(`${BASE_URL}/pharmacy/prescriptions/all`, {
    headers: authHeaders(token, tenantId),
  });
  if (res.status === 404 || res.status === 405) {
    const fallback = await fetch(`${BASE_URL}/pharmacy/prescriptions`, {
      headers: authHeaders(token, tenantId),
    });
    if (!fallback.ok) throw new Error(`${fallback.status}`);
    return fallback.json();
  }
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
};

const patchStatus = async (token, tenantId, id, status) => {
  const res = await fetch(`${BASE_URL}/pharmacy/prescriptions/${id}/status`, {
    method: 'PATCH',
    headers: authHeaders(token, tenantId),
    body: JSON.stringify({
      status,
      remarks: status === 'APPROVED' ? 'Approved by pharmacist' : 'Rejected by pharmacist',
    }),
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const timeAgo = (iso) => {
  const diff = Math.round((Date.now() - new Date(iso)) / 60000);
  if (diff < 1)    return 'just now';
  if (diff < 60)   return `${diff}m ago`;
  if (diff < 1440) return `${Math.round(diff / 60)}h ago`;
  return `${Math.round(diff / 1440)}d ago`;
};

const initials = (name = '') =>
  name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('');

const TABS = ['PENDING', 'APPROVED', 'REJECTED'];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const StatusBadge = ({ status }) => {
  const styles = {
    PENDING:  { background: '#faeeda', color: '#854f0b', border: '0.5px solid #fac775' },
    APPROVED: { background: '#eaf3de', color: '#3b6d11', border: '0.5px solid #97c459' },
    REJECTED: { background: '#fcebeb', color: '#a32d2d', border: '0.5px solid #f09595' },
  };
  return (
    <span style={{
      ...styles[status],
      fontSize: '11px', fontWeight: 500,
      padding: '3px 8px', borderRadius: '6px',
      display: 'inline-block',
    }}>
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
};

const FileIcon = ({ type }) => (
  <div style={{
    width: 32, height: 32, borderRadius: 8,
    background: '#e6f1fb', display: 'flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  }}>
    {type === 'application/pdf' ? (
      <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="#185fa5" strokeWidth="1.5">
        <rect x="2" y="1" width="10" height="14" rx="1.5"/>
        <path d="M5 5h4M5 8h4M5 11h2" strokeLinecap="round"/>
      </svg>
    ) : (
      <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="#185fa5" strokeWidth="1.5">
        <rect x="1" y="1" width="14" height="14" rx="1.5"/>
        <path d="M1 10.5l4-4 3 3 2-2 5 5" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="11" cy="5" r="1.5"/>
      </svg>
    )}
  </div>
);

const AvatarCircle = ({ name }) => (
  <div style={{
    width: 42, height: 42, borderRadius: '50%',
    background: '#e6f1fb', color: '#185fa5',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 500, flexShrink: 0,
  }}>
    {initials(name) || '?'}
  </div>
);

const Toast = ({ message, type, visible }) => (
  <div style={{
    position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
    padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 500,
    background: type === 'success' ? '#eaf3de' : type === 'error' ? '#fcebeb' : '#faeeda',
    color:      type === 'success' ? '#3b6d11'  : type === 'error' ? '#a32d2d'  : '#854f0b',
    border: `0.5px solid ${type === 'success' ? '#97c459' : type === 'error' ? '#f09595' : '#fac775'}`,
    boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(8px)',
    transition: 'opacity 0.2s, transform 0.2s',
    pointerEvents: 'none',
  }}>
    {message}
  </div>
);

const SkeletonCard = () => (
  <div style={{
    background: 'var(--surface, #fff)', border: '0.5px solid #e4e2db',
    borderRadius: 14, padding: '1rem 1.25rem',
  }}>
    {[['60%', 14], ['40%', 12], ['100%', 10], ['100%', 32]].map(([w, h], i) => (
      <div key={i} style={{
        width: w, height: h, borderRadius: 6, marginBottom: 10,
        background: 'linear-gradient(90deg,#f0eeea 25%,#e8e6e1 50%,#f0eeea 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.4s infinite',
      }}/>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// RxCard
// ---------------------------------------------------------------------------
const RxCard = ({ rx, onAct }) => {
  const [acting, setActing] = useState(false);

  const handle = async (status) => {
    setActing(true);
    await onAct(rx.id, status);
    setActing(false);
  };

  return (
    <div style={{
      background: 'var(--surface, #fff)',
      border: `0.5px solid ${rx.status === 'APPROVED' ? '#97c459' : rx.status === 'REJECTED' ? '#f09595' : '#e4e2db'}`,
      borderRadius: 14, padding: '1rem 1.25rem',
      transition: 'border-color .15s, box-shadow .15s',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <AvatarCircle name={rx.patientName || rx.fileName} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text, #1a1917)' }}>
            {rx.patientName || 'Patient'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-2, #6b6860)', marginTop: 2 }}>
            {rx.patientPhone || '—'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <StatusBadge status={rx.status} />
            <span style={{ fontSize: 11, color: 'var(--text-3, #9e9c96)' }}>
              {timeAgo(rx.uploadedAt)}
            </span>
          </div>
        </div>
      </div>

      {/* File row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        marginTop: 12, padding: '8px 10px',
        background: 'var(--bg, #f5f4f0)', borderRadius: 8,
        border: '0.5px solid #e4e2db',
      }}>
        <FileIcon type={rx.fileType} />
        <span style={{
          fontSize: 13, flex: 1, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: 'var(--text, #1a1917)',
        }}>
          {rx.fileName}
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-3, #9e9c96)', whiteSpace: 'nowrap' }}>
          {rx.fileType === 'application/pdf' ? 'PDF' : rx.fileType?.split('/')[1]?.toUpperCase() || 'FILE'}
        </span>
      </div>

      {/* Notes */}
      {rx.notes && (
        <div style={{
          marginTop: 10, fontSize: 12, fontStyle: 'italic',
          color: 'var(--text-2, #6b6860)',
          borderLeft: '2px solid #e4e2db', paddingLeft: 10,
        }}>
          "{rx.notes}"
        </div>
      )}

      {/* Actions */}
      {rx.status === 'PENDING' && (
        <div style={{
          display: 'flex', gap: 8, marginTop: 14,
          paddingTop: 14, borderTop: '0.5px solid #e4e2db',
        }}>
          <button
            disabled={acting}
            onClick={() => handle('REJECTED')}
            style={{
              flex: 1, padding: '7px 0', fontSize: 13, fontWeight: 500,
              borderRadius: 8, cursor: acting ? 'not-allowed' : 'pointer',
              border: '0.5px solid #f09595', background: '#fcebeb', color: '#a32d2d',
              opacity: acting ? 0.6 : 1, transition: 'opacity .15s',
            }}>
            Reject
          </button>
          <button
            disabled={acting}
            onClick={() => handle('APPROVED')}
            style={{
              flex: 2, padding: '7px 0', fontSize: 13, fontWeight: 500,
              borderRadius: 8, cursor: acting ? 'not-allowed' : 'pointer',
              border: '0.5px solid #97c459', background: '#eaf3de', color: '#3b6d11',
              opacity: acting ? 0.6 : 1, transition: 'opacity .15s',
            }}>
            {acting ? 'Saving…' : 'Approve'}
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const PharmacistDashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { tenant } = useTenant();

  // Guard: ADMIN only (no PHARMACIST role exists)
  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    if (user.role !== 'ADMIN') { navigate('/unauthorized', { replace: true }); }
  }, [user, navigate]);

  const [prescriptions, setPrescriptions] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [activeTab,     setActiveTab]     = useState('PENDING');
  const [toast,         setToast]         = useState({ visible: false, message: '', type: 'success' });
  const [lastRefresh,   setLastRefresh]   = useState(null);


  const load = useCallback(async (silent = false) => {
    if (!token || !tenant?.id) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await fetchAllPrescriptions(token, tenant.id);
      setPrescriptions(data);
      setLastRefresh(new Date());
    } catch (e) {
      setError(`Failed to load prescriptions (${e.message})`);
    } finally {
      setLoading(false);
    }
  }, [token, tenant?.id]);

  // Initial load
  useEffect(() => { load(); }, [load]);

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 2500);
  };

  const handleAct = async (id, status) => {
    try {
      const updated = await patchStatus(token, tenant.id, id, status);
      setPrescriptions(prev =>
        prev.map(p => p.id === updated.id ? { ...p, status: updated.status } : p)
      );
      showToast(
        status === 'APPROVED' ? 'Prescription approved' : 'Prescription rejected',
        status === 'APPROVED' ? 'success' : 'error'
      );
    } catch (e) {
      showToast(`Action failed (${e.message})`, 'error');
    }
  };

  const counts = {
    PENDING:  prescriptions.filter(p => p.status === 'PENDING').length,
    APPROVED: prescriptions.filter(p => p.status === 'APPROVED').length,
    REJECTED: prescriptions.filter(p => p.status === 'REJECTED').length,
  };

  const filtered = prescriptions.filter(p => p.status === activeTab);

  if (!user) return null;

  return (
    <div className="page">
      <main className="page-main">

        {/* Breadcrumb */}
        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <span className="bc-current">Prescription Review</span>
        </div>

        {/* Header */}
        <div className="fade-up" style={{ marginBottom: '1.5rem' }}>
          <h1 className="t-h1" style={{ marginBottom: 4 }}>℞ Prescription Review</h1>
          <p className="t-body-sm" style={{ color: 'var(--text-2, #6b6860)' }}>
            Review and approve incoming patient prescriptions
            {lastRefresh && (
              <span style={{ fontSize: 12, marginLeft: 10, opacity: 0.6 }}>
                · refreshed {timeAgo(lastRefresh.toISOString())}
              </span>
            )}
          </p>
        </div>

        {/* Stat cards */}
        <div className="rx-stat-grid">
          {[
            { label: 'Pending review', key: 'PENDING',  color: '#854f0b', bg: '#faeeda' },
            { label: 'Approved',       key: 'APPROVED', color: '#3b6d11', bg: '#eaf3de' },
            { label: 'Rejected',       key: 'REJECTED', color: '#a32d2d', bg: '#fcebeb' },
          ].map(({ label, key, color, bg }) => (
            <div key={key} style={{
              background: activeTab === key ? bg : 'var(--surface-secondary, #f5f4f0)',
              borderRadius: 10, padding: '12px 16px',
              border: `0.5px solid ${activeTab === key ? color + '44' : '#e4e2db'}`,
              cursor: 'pointer', transition: 'all .15s',
            }} onClick={() => setActiveTab(key)}>
              <div style={{ fontSize: 12, color: 'var(--text-2, #6b6860)', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 26, fontWeight: 500, color: activeTab === key ? color : 'var(--text, #1a1917)' }}>
                {counts[key]}
              </div>
            </div>
          ))}
        </div>

        {/* Tab bar */}
        <div style={{
          display: 'flex', gap: 4, marginBottom: '1.25rem',
          background: 'var(--surface-secondary, #f5f4f0)',
          borderRadius: 10, padding: 3, alignSelf: 'flex-start',
          width: 'fit-content',
        }}>
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              fontSize: 13, padding: '5px 16px', borderRadius: 8, border: 'none',
              cursor: 'pointer', transition: 'all .15s',
              background: activeTab === tab ? 'var(--surface, #fff)' : 'transparent',
              color: activeTab === tab ? 'var(--text, #1a1917)' : 'var(--text-2, #6b6860)',
              fontWeight: activeTab === tab ? 500 : 400,
              boxShadow: activeTab === tab ? '0 0 0 0.5px #e4e2db' : 'none',
            }}>
              {tab.charAt(0) + tab.slice(1).toLowerCase()}
              {counts[tab] > 0 && (
                <span style={{
                  marginLeft: 6, fontSize: 11, fontWeight: 500,
                  background: tab === 'PENDING' ? '#faeeda' : tab === 'APPROVED' ? '#eaf3de' : '#fcebeb',
                  color:      tab === 'PENDING' ? '#854f0b' : tab === 'APPROVED' ? '#3b6d11' : '#a32d2d',
                  padding: '1px 6px', borderRadius: 20,
                }}>
                  {counts[tab]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="error-state" style={{ marginBottom: '1rem' }}>
            <span>⚠️</span> {error}
            <button className="t-link" style={{ marginLeft: 12 }} onClick={() => load()}>Retry</button>
          </div>
        )}

        {/* List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)
          ) : filtered.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '3.5rem 1rem',
              color: 'var(--text-2, #6b6860)', fontSize: 14,
            }}>
              <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.3 }}>℞</div>
              No {activeTab.toLowerCase()} prescriptions
            </div>
          ) : (
            filtered.map((rx, i) => (
              <div key={rx.id} style={{ animation: `fadeUp .3s ${i * 40}ms ease both` }}>
                <RxCard rx={rx} onAct={handleAct} />
              </div>
            ))
          )}
        </div>

      </main>

      <Toast {...toast} />

      <style>{`
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
};

export default PharmacistDashboard;